import { useCallback, useMemo, useRef, useState } from "react";
import ModalGallery from "../components/Modal/ModalGallery";
import ModalCreateWithIA from "../components/Modal/ModalCreateWithIA";
import ShareModalMultiple from "../components/Gallery/ShareModalMultiple/ShareModalMultiple";
import {
  CONTENT_MODAL_TYPE,
  ContentModalsContext,
} from "./contentModalsContext";

const ContentModalInstance = ({ type, props }) => {
  switch (type) {
    case CONTENT_MODAL_TYPE.GALLERY:
      return <ModalGallery {...props} />;
    case CONTENT_MODAL_TYPE.CREATE_WITH_IA:
      return <ModalCreateWithIA {...props} />;
    case CONTENT_MODAL_TYPE.SHARE:
      return <ShareModalMultiple {...props} />;
    default: {
      const exhaustiveCheck = type;
      throw new Error(`Unhandled content modal type: ${exhaustiveCheck}`);
    }
  }
};

export const ContentModalsProvider = ({ children }) => {
  const nextIdRef = useRef(0);
  const [stack, setStack] = useState([]);

  const close = useCallback((id) => {
    setStack((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const push = useCallback((type, props = {}) => {
    const id = `content-modal-${++nextIdRef.current}`;
    const { onCloseModal, ...rest } = props;

    setStack((current) => [
      ...current,
      {
        id,
        type,
        props: {
          ...rest,
          modalOpen: true,
          onCloseModal: (...args) => {
            close(id);
            onCloseModal?.(...args);
          },
        },
      },
    ]);

    return id;
  }, [close]);

  const openGallery = useCallback(
    (props) => push(CONTENT_MODAL_TYPE.GALLERY, props),
    [push]
  );
  const openCreateWithIA = useCallback(
    (props) => push(CONTENT_MODAL_TYPE.CREATE_WITH_IA, props),
    [push]
  );
  const openShare = useCallback(
    (props) => push(CONTENT_MODAL_TYPE.SHARE, props),
    [push]
  );

  const value = useMemo(
    () => ({
      openGallery,
      openCreateWithIA,
      openShare,
      close,
    }),
    [close, openCreateWithIA, openGallery, openShare]
  );

  return (
    <ContentModalsContext.Provider value={value}>
      {children}
      {stack.map((entry) => (
        <ContentModalInstance
          key={entry.id}
          type={entry.type}
          props={entry.props}
        />
      ))}
    </ContentModalsContext.Provider>
  );
};
